import { useEffect, useState } from 'react';

import { apiFetch, isAdsEnabled, isInteriorEnabled, isVideoEnabled } from '../../utils/api';
import { fetchAllJobsInRange, calculateJobCost } from '../../hooks/useStats';
import { fetchPricingSettings } from '../../hooks/useSettings';
import type { DateRange, HistoricalJob } from '../../types';

import { listAllHistoryJobs as listAllVideoHistory } from '../../video/api';
import { calculateVideoJobCost } from '../../video/hooks/useVideoStats';
import type { Job as VideoJob } from '../../video/types';

import { listHistoryJobs as listInteriorHistory } from '../../interior/api';
import { calculateInteriorJobCost } from '../../interior/hooks/useInteriorStats';
import type { Job as InteriorJob } from '../../interior/types';

import { listHistoryJobs as listAdsHistory } from '../../ads/api';
import { calculateAdsJobCost } from '../../ads/hooks/useAdsStats';
import type { Job as AdsJob } from '../../ads/types';

import { fetchStats, fetchStatsByJob } from '../../apigen/api';

import type { CostItem, MerchCostItem, MerchCostTotals, ServiceCost } from '../types';

// ---------------------------------------------------------------------------
// GCP flat cost per scan — stored as a single row in the lightning
// `pricing_settings` table (provider `infra`, key `gcp_cost_per_scan`) so it
// is configurable without a new table/endpoint. The lightning pricing UI keys
// off known FAL/OpenAI/Gemini endpoints, so this extra row is inert there.
// ---------------------------------------------------------------------------

export const GCP_COST_PROVIDER = 'infra';
export const GCP_COST_KEY = 'gcp_cost_per_scan';
/** Fallback used until The Master configures a real figure. */
export const DEFAULT_GCP_COST_PER_SCAN = 0.1;

interface RawPricingRow {
  provider: string;
  endpoint_or_model: string;
  price_per_call: number | null;
}

export async function fetchGcpCostPerScan(): Promise<number> {
  const res = await apiFetch('/api/settings/pricing');
  if (!res.ok) return DEFAULT_GCP_COST_PER_SCAN;
  const rows = (await res.json()) as RawPricingRow[];
  const row = rows.find(
    (r) => r.provider === GCP_COST_PROVIDER && r.endpoint_or_model === GCP_COST_KEY,
  );
  return row?.price_per_call != null ? Number(row.price_per_call) : DEFAULT_GCP_COST_PER_SCAN;
}

export async function saveGcpCostPerScan(value: number): Promise<void> {
  const res = await apiFetch('/api/settings/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      settings: [
        {
          provider: GCP_COST_PROVIDER,
          endpoint_or_model: GCP_COST_KEY,
          price_per_call: value,
          price_per_input_token: null,
          price_per_output_token: null,
        },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to save GCP cost (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// Taggers flat cost per scan — stored the same way as the GCP cost (an `infra`
// row in `pricing_settings`, key `taggers_cost_per_scan`), and allocated flatly
// to every completed merch scan alongside the GCP cost.
// ---------------------------------------------------------------------------

export const TAGGERS_COST_PROVIDER = 'infra';
export const TAGGERS_COST_KEY = 'taggers_cost_per_scan';
/** Fallback used until The Master configures a real figure. */
export const DEFAULT_TAGGERS_COST_PER_SCAN = 0;

export async function fetchTaggersCostPerScan(): Promise<number> {
  const res = await apiFetch('/api/settings/pricing');
  if (!res.ok) return DEFAULT_TAGGERS_COST_PER_SCAN;
  const rows = (await res.json()) as RawPricingRow[];
  const row = rows.find(
    (r) => r.provider === TAGGERS_COST_PROVIDER && r.endpoint_or_model === TAGGERS_COST_KEY,
  );
  return row?.price_per_call != null ? Number(row.price_per_call) : DEFAULT_TAGGERS_COST_PER_SCAN;
}

export async function saveTaggersCostPerScan(value: number): Promise<void> {
  const res = await apiFetch('/api/settings/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      settings: [
        {
          provider: TAGGERS_COST_PROVIDER,
          endpoint_or_model: TAGGERS_COST_KEY,
          price_per_call: value,
          price_per_input_token: null,
          price_per_output_token: null,
        },
      ],
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to save taggers cost (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function toMs(value: string | null | undefined, fallback: number): number {
  if (!value) return fallback;
  const t = Date.parse(value);
  return Number.isNaN(t) ? fallback : t;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function sumCost(items: CostItem[]): number {
  return items.reduce((acc, it) => acc + it.cost, 0);
}

async function fetchAllInteriorJobs(dateRange: DateRange): Promise<InteriorJob[]> {
  const all: InteriorJob[] = [];
  const pageSize = 500;
  let page = 1;
  let totalPages = 1;
  do {
    const resp = await listInteriorHistory(dateRange, null, page, pageSize);
    all.push(...resp.jobs);
    totalPages = resp.total_pages;
    if (resp.jobs.length === 0) break;
    page += 1;
  } while (page <= totalPages);
  return all;
}

async function fetchAllAdsJobs(dateRange: DateRange): Promise<AdsJob[]> {
  const all: AdsJob[] = [];
  const pageSize = 500;
  let page = 1;
  let totalPages = 1;
  do {
    const resp = await listAdsHistory(dateRange, null, page, pageSize);
    all.push(...resp.jobs);
    totalPages = resp.total_pages;
    if (resp.jobs.length === 0) break;
    page += 1;
  } while (page <= totalPages);
  return all;
}

/**
 * Pick a gen-api server-side bucket width (seconds) that keeps the series
 * payload bounded (~<=1500 points) for the requested window. The Cost pages
 * re-bucket client-side anyway, so a coarser server bucket is lossless here.
 */
function genapiBucketSeconds(from: Date, to: Date): number {
  const windowSec = Math.max(1, Math.round((to.getTime() - from.getTime()) / 1000));
  return Math.min(86_400, Math.max(60, Math.ceil(windowSec / 1500)));
}

// ---------------------------------------------------------------------------
// per-service builders (each fails independently → non-fatal warning)
// ---------------------------------------------------------------------------

async function buildMerch(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const [pricing, jobs] = await Promise.all([
      fetchPricingSettings().catch(() => []),
      fetchAllJobsInRange(dateRange),
    ]);
    const completed = jobs.filter((j: HistoricalJob) => j.status === 'completed');
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: calculateJobCost(j, pricing),
    }));
    return { key: 'merch', label: 'Merch', unit: 'scans', count: completed.length, totalCost: sumCost(items), items };
  } catch (e) {
    return { key: 'merch', label: 'Merch', unit: 'scans', count: 0, totalCost: 0, items: [], warning: errMsg(e) };
  }
}

async function buildVideo(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await listAllVideoHistory(dateRange);
    const completed = jobs.filter((j: VideoJob) => j.status === 'completed');
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: calculateVideoJobCost(j),
    }));
    return { key: 'video', label: '360 Video', unit: 'scans', count: completed.length, totalCost: sumCost(items), items };
  } catch (e) {
    return { key: 'video', label: '360 Video', unit: 'scans', count: 0, totalCost: 0, items: [], warning: errMsg(e) };
  }
}

async function buildInterior(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await fetchAllInteriorJobs(dateRange);
    const completed = jobs.filter((j) => j.status === 'completed');
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: calculateInteriorJobCost(j),
    }));
    return { key: 'interior', label: 'Interior', unit: 'scans', count: completed.length, totalCost: sumCost(items), items };
  } catch (e) {
    return { key: 'interior', label: 'Interior', unit: 'scans', count: 0, totalCost: 0, items: [], warning: errMsg(e) };
  }
}

async function buildAds(dateRange: DateRange, defaultTs: number): Promise<ServiceCost> {
  try {
    const jobs = await fetchAllAdsJobs(dateRange);
    const completed = jobs.filter((j) => j.status === 'completed');
    const items: CostItem[] = completed.map((j) => ({
      t: toMs(j.completed_at ?? j.created_at, defaultTs),
      cost: calculateAdsJobCost(j),
    }));
    return { key: 'ads', label: 'Ads', unit: 'scans', count: completed.length, totalCost: sumCost(items), items };
  } catch (e) {
    return { key: 'ads', label: 'Ads', unit: 'scans', count: 0, totalCost: 0, items: [], warning: errMsg(e) };
  }
}

async function buildGenApi(from: Date, to: Date): Promise<ServiceCost> {
  try {
    const stats = await fetchStats({ from, to, bucketSeconds: genapiBucketSeconds(from, to) });
    const items: CostItem[] = stats.series.map((p) => ({
      t: p.ts ? Date.parse(p.ts) : from.getTime(),
      cost: p.cost_usd || 0,
    }));
    return {
      key: 'genapi',
      label: 'Gen-API',
      unit: 'calls',
      count: stats.totals.total,
      totalCost: stats.totals.cost_usd,
      items,
    };
  } catch (e) {
    return {
      key: 'genapi',
      label: 'Gen-API',
      unit: 'calls',
      count: 0,
      totalCost: 0,
      items: [],
      warning: `gen-api unreachable: ${errMsg(e)}`,
    };
  }
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

export interface AllServicesCostResult {
  services: ServiceCost[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Cost across every service (merch/360/interior/ads/gen-api) for a window.
 * Each service is fetched independently so one being down (or disabled in a
 * dev build) never blanks the whole page.
 */
export function useAllServicesCost(range: { from: Date; to: Date }): AllServicesCostResult {
  const [services, setServices] = useState<ServiceCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fromMs = range.from.getTime();
  const toMs2 = range.to.getTime();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const from = new Date(fromMs);
    const to = new Date(toMs2);
    const dateRange: DateRange = { from, to };
    const defaultTs = toMs2;

    const builders: Promise<ServiceCost>[] = [buildMerch(dateRange, defaultTs)];
    if (isVideoEnabled()) builders.push(buildVideo(dateRange, defaultTs));
    if (isInteriorEnabled()) builders.push(buildInterior(dateRange, defaultTs));
    if (isAdsEnabled()) builders.push(buildAds(dateRange, defaultTs));
    builders.push(buildGenApi(from, to));

    Promise.all(builders)
      .then((results) => {
        if (!cancelled) setServices(results);
      })
      .catch((e) => {
        if (!cancelled) setError(errMsg(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromMs, toMs2, reloadKey]);

  return { services, loading, error, reload: () => setReloadKey((k) => k + 1) };
}

export interface MerchCostResult {
  items: MerchCostItem[];
  totals: MerchCostTotals;
  gcpCostPerScan: number;
  taggersCostPerScan: number;
  genApiWarning: string | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  saveGcp: (value: number) => Promise<void>;
  saveTaggers: (value: number) => Promise<void>;
}

const EMPTY_TOTALS: MerchCostTotals = {
  scans: 0,
  merchGen: 0,
  genApi: 0,
  gcp: 0,
  taggers: 0,
  grand: 0,
  avgPerScan: 0,
};

/**
 * Per-merch-scan cost combining generation cost (8 angles), correlated
 * gen-api spend for the same job id, and a flat configurable GCP cost.
 */
export function useMerchCost(range: { from: Date; to: Date }): MerchCostResult {
  const [items, setItems] = useState<MerchCostItem[]>([]);
  const [totals, setTotals] = useState<MerchCostTotals>(EMPTY_TOTALS);
  const [gcpCostPerScan, setGcpCostPerScan] = useState<number>(DEFAULT_GCP_COST_PER_SCAN);
  const [taggersCostPerScan, setTaggersCostPerScan] = useState<number>(DEFAULT_TAGGERS_COST_PER_SCAN);
  const [genApiWarning, setGenApiWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const fromMs = range.from.getTime();
  const toMs2 = range.to.getTime();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGenApiWarning(null);
    const from = new Date(fromMs);
    const to = new Date(toMs2);
    const dateRange: DateRange = { from, to };

    (async () => {
      try {
        const [pricing, merchJobs, gcp, taggers] = await Promise.all([
          fetchPricingSettings().catch(() => []),
          fetchAllJobsInRange(dateRange),
          fetchGcpCostPerScan().catch(() => DEFAULT_GCP_COST_PER_SCAN),
          fetchTaggersCostPerScan().catch(() => DEFAULT_TAGGERS_COST_PER_SCAN),
        ]);

        // gen-api correlation is best-effort (separate service).
        const byJob = new Map<string, number>();
        let warning: string | null = null;
        try {
          const resp = await fetchStatsByJob({ from, to });
          for (const row of resp.by_job) byJob.set(row.job_id, row.cost_usd);
        } catch (e) {
          warning = `gen-api unreachable: ${errMsg(e)}`;
        }

        const completed = merchJobs.filter((j: HistoricalJob) => j.status === 'completed');
        const built: MerchCostItem[] = completed.map((j) => {
          const merchGen = calculateJobCost(j, pricing);
          const genApi = byJob.get(j.job_id) ?? 0;
          const total = merchGen + genApi + gcp + taggers;
          return {
            jobId: j.job_id,
            scanId: j.scan_id,
            t: toMs(j.completed_at ?? j.created_at, toMs2),
            merchGen,
            genApi,
            gcp,
            taggers,
            total,
          };
        });

        const agg = built.reduce(
          (acc, it) => {
            acc.merchGen += it.merchGen;
            acc.genApi += it.genApi;
            acc.gcp += it.gcp;
            acc.taggers += it.taggers;
            return acc;
          },
          { merchGen: 0, genApi: 0, gcp: 0, taggers: 0 },
        );
        const grand = agg.merchGen + agg.genApi + agg.gcp + agg.taggers;
        const nextTotals: MerchCostTotals = {
          scans: built.length,
          merchGen: agg.merchGen,
          genApi: agg.genApi,
          gcp: agg.gcp,
          taggers: agg.taggers,
          grand,
          avgPerScan: built.length ? grand / built.length : 0,
        };

        if (!cancelled) {
          setItems(built);
          setTotals(nextTotals);
          setGcpCostPerScan(gcp);
          setTaggersCostPerScan(taggers);
          setGenApiWarning(warning);
        }
      } catch (e) {
        if (!cancelled) {
          setError(errMsg(e));
          setItems([]);
          setTotals(EMPTY_TOTALS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fromMs, toMs2, reloadKey]);

  const saveGcp = async (value: number) => {
    await saveGcpCostPerScan(value);
    setReloadKey((k) => k + 1);
  };

  const saveTaggers = async (value: number) => {
    await saveTaggersCostPerScan(value);
    setReloadKey((k) => k + 1);
  };

  return {
    items,
    totals,
    gcpCostPerScan,
    taggersCostPerScan,
    genApiWarning,
    loading,
    error,
    reload: () => setReloadKey((k) => k + 1),
    saveGcp,
    saveTaggers,
  };
}
